"use client";

import React, { useState, useRef, useEffect } from "react";
import { RangeCalendar, Button } from "@heroui/react";
import { CalendarDate, parseDate, getLocalTimeZone, DateValue } from "@internationalized/date";
import { useTranslations } from "../../contexts/translation.context";

export interface DateFilterSelectProps {
  placeholder: string;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  onCustomDateChange?: (startDate: string, endDate: string) => void;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'campaign';
  customStartDate?: string;
  customEndDate?: string;
}

const DateFilterSelect: React.FC<DateFilterSelectProps> = ({
  placeholder,
  selectedKey,
  onSelectionChange,
  onCustomDateChange,
  startContent,
  className = "",
  disabled = false,
  size = "sm",
  variant = "default",
  customStartDate,
  customEndDate,
}) => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{start: CalendarDate, end: CalendarDate} | null>(
    customStartDate && customEndDate ? {
      start: parseDate(customStartDate),
      end: parseDate(customEndDate)
    } : null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dateOptions = [
    { key: 'today', label: t('statistics.filters.today') },
    { key: 'week', label: t('statistics.filters.week') },
    { key: 'month', label: t('statistics.filters.month') },
    { key: 'quarter', label: t('statistics.filters.quarter') },
    { key: 'halfyear', label: t('statistics.filters.halfyear') },
    { key: 'year', label: t('statistics.filters.year') },
    { key: 'all', label: t('statistics.filters.allTime') },
    { key: 'custom', label: t('statistics.filters.customRange') },
  ];

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: "bg-[#f4f4f5] border-none rounded-[8px] !px-[12px] !min-h-[36px] !h-[36px]",
          value: "text-[14px] !text-[#71717a]"
        };
      default:
        return {
          trigger: "h-9 bg-gray-100 border-none rounded-[8px] !px-[12px] hover:bg-gray-200 focus:border-blue-500",
          value: "text-[14px] !text-[#71717a]"
        };
    }
  };

  const variantStyles = getVariantStyles();

  const getSelectedLabel = () => {
    const selectedOption = dateOptions.find(option => option.key === selectedKey);
    return selectedOption ? selectedOption.label : placeholder;
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (key: string) => {
    if (key !== 'custom') {
      onSelectionChange(key);
      setIsOpen(false);
    } else {
      onSelectionChange(key);
      // Reset date selection when opening custom date picker
      if (!customStartDate || !customEndDate) {
        setSelectedDateRange(null);
      }
      // Keep dropdown open for custom date selection
    }
  };

  const handleCustomDateApply = () => {
    if (selectedDateRange && onCustomDateChange) {
      const startDateString = selectedDateRange.start.toString();
      const endDateString = selectedDateRange.end.toString();
      onCustomDateChange(startDateString, endDateString);
      setIsOpen(false);
    }
  };

  const handleCustomDateCancel = () => {
    setSelectedDateRange(
      customStartDate && customEndDate ? {
        start: parseDate(customStartDate),
        end: parseDate(customEndDate)
      } : null
    );
    setIsOpen(false);
  };

  const handleDateRangeChange = (value: any) => {
    if (value && value.start && value.end) {
      setSelectedDateRange({
        start: value.start as CalendarDate,
        end: value.end as CalendarDate
      });
    } else if (value && value.start && !value.end) {
      // Только начальная дата выбрана
      setSelectedDateRange(null);
    }
  };

  const formatDisplayDate = () => {
    if (selectedKey === 'custom' && selectedDateRange) {
      const startDate = selectedDateRange.start.toDate(getLocalTimeZone());
      const endDate = selectedDateRange.end.toDate(getLocalTimeZone());
      return `${startDate.toLocaleDateString('ru-RU')} - ${endDate.toLocaleDateString('ru-RU')}`;
    }
    return getSelectedLabel();
  };

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          ${variantStyles.trigger}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          flex items-center gap-[6px] whitespace-nowrap ${className}
        `}
      >
        {startContent && <span className="flex-shrink-0">{startContent}</span>}
        <span className={selectedKey !== 'all' ? 'text-[14px] !text-[#11181C]' : variantStyles.value}>{formatDisplayDate()}</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <polyline points="6,9 12,15 18,9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-[12px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.1)] border border-[#e0e0e0] z-50 overflow-hidden">
          <div className="flex">
            {/* Left side - Options */}
            <div className="w-[200px] p-[8px] border-r border-[#e0e0e0]">
              {dateOptions.map((option) => (
                <div
                  key={option.key}
                  onClick={() => handleOptionSelect(option.key)}
                  className={`
                    flex items-center px-[8px] py-0 h-[32px] rounded-[8px] cursor-pointer hover:bg-[#eeeeef]
                    ${selectedKey === option.key ? 'bg-[#eeeeef]' : ''}
                  `}
                >
                  <span className="text-[14px] text-[#11181c] leading-[18px]">
                    {option.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Right side - Calendar (only show when custom is selected) */}
            {selectedKey === 'custom' && (
              <div className="w-[616px] bg-white">
                <div className="w-[616px] h-[322px]">
                  <RangeCalendar
                    aria-label="Выбор периода"
                    value={selectedDateRange}
                    onChange={handleDateRangeChange}
                    visibleMonths={2}
                    classNames={{
                      base: "w-[616px] h-[322px] pt-[16px] bg-transparent border-none shadow-none",
                      content: "w-[616px] h-[322px] border-none shadow-none p-0 flex flex-col",
                      // header: "border-none bg-transparent px-4 pt-4 pb-2 h-[32px] flex-shrink-0",
                      // title: "text-[16px] font-medium text-zinc-500",
                      // gridWrapper: "bg-transparent border-none px-4 flex-1 overflow-hidden",
                      // gridHeader: "bg-transparent h-[40px] flex-shrink-0",
                      // gridHeaderRow: "mb-2 h-[40px]",
                      // gridHeaderCell: "text-[16px] font-medium text-zinc-400 w-[39.43px] h-[40px] flex items-center justify-center",
                      // gridBody: "bg-transparent flex-1",
                      // gridBodyRow: "h-[56px]",
                      // cell: "w-[39.43px] h-[56px] p-0 flex items-center justify-center",
                      // cellButton: [
                      //   "w-[39.43px] h-[56px] text-[20px] font-normal border-none bg-transparent flex items-center justify-center",
                      //   "data-[today=true]:bg-[#9675ce] data-[today=true]:text-white data-[today=true]:rounded-full data-[today=true]:w-[32px] data-[today=true]:h-[32px]",
                      //   "data-[selected=true]:bg-[#9675ce] data-[selected=true]:text-white data-[selected=true]:rounded-full data-[selected=true]:w-[32px] data-[selected=true]:h-[32px]",
                      //   "data-[outside-month=true]:text-[#cececf]",
                      //   "hover:bg-gray-100 hover:rounded-lg hover:w-[32px] hover:h-[32px]",
                      //   "text-[#11181c]"
                      // ],
                    }}
                  />
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-[8px] mt-[-16px] px-[24px] pb-[16px] ">
                  <div className="flex-1 text-[14px] text-[#11181c] leading-[18px]">
                    {!selectedDateRange && (
                      <span className="text-gray-400">Выберите диапазон дат</span>
                    )}
                    {selectedDateRange && 
                      `${selectedDateRange.start.toDate(getLocalTimeZone()).toLocaleDateString('ru-RU')} - ${selectedDateRange.end.toDate(getLocalTimeZone()).toLocaleDateString('ru-RU')}`
                    }
                  </div>
                  <Button
                    variant="flat"
                    color="danger"
                    size="md"
                    className="bg-[rgba(243,18,96,0.2)] text-[#f31260] h-[40px] px-[16px] rounded-[12px]"
                    onPress={handleCustomDateCancel}
                  >
                    Отмена
                  </Button>
                  <Button
                    className="bg-[#9675ce] text-white h-[40px] px-[14px] py-[10px] rounded-[12px]"
                    onPress={handleCustomDateApply}
                    isDisabled={!selectedDateRange}
                  >
                    Применить
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterSelect;
